import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Alert, Button, Card, Input, Popconfirm, Select, Space, Tag, Typography, message } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Dialog as ZhDialog, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { Permission } from "@/views/components/Permission";
import { useUserStore } from "@/views/stores";

export interface AiHotWordRow {
    id: number;
    hotWord?: string;
    weight?: number | string;
    state?: number | string;
}

export interface AiHotWordQuery {
    page: number;
    size: number;
    keyWord?: string;
    weights?: number[];
    state?: number;
    sortKey?: string;
    sortOrder?: string;
}

export interface AiHotWordPageResult {
    list?: AiHotWordRow[];
    total?: number;
}

export interface AiHotWordForm {
    hotWord: string;
    weight: number;
}

export interface AiHotWordPageProps {
    loadHotWords?: (query: AiHotWordQuery) => Promise<AiHotWordPageResult>;
    create?: (form: AiHotWordForm) => Promise<unknown>;
    update?: (id: number, form: AiHotWordForm) => Promise<unknown>;
    remove?: (id: number) => Promise<unknown>;
    synchronize?: () => Promise<unknown>;
}

const defaultQuery: AiHotWordQuery = { page: 1, size: 10 };
const weightOptions = Array.from({ length: 12 }, (_value, index) => index - 6);

/** 复用 Vue 热词分页接口。 */
export async function loadAiHotWords(query: AiHotWordQuery): Promise<AiHotWordPageResult> {
    const response = await request.service.get<AiHotWordPageResult>("/admin/hot-word/paging", { params: query });
    return response.data;
}

/** 复用 Vue 新增热词接口。 */
export async function createAiHotWord(form: AiHotWordForm): Promise<unknown> {
    const response = await request.service.post("/admin/hot-word", form);
    return response.data;
}

/** 复用 Vue 编辑热词接口。 */
export async function updateAiHotWord(id: number, form: AiHotWordForm): Promise<unknown> {
    const response = await request.service.put(`/admin/hot-word/${id}`, form);
    return response.data;
}

/** 复用 Vue 删除热词接口。 */
export async function deleteAiHotWord(id: number): Promise<unknown> {
    const response = await request.service.delete(`/admin/hot-word/${id}`);
    return response.data;
}

/** 复用 Vue 一键同步接口。 */
export async function synchronizeAiHotWords(): Promise<unknown> {
    const response = await request.service.post("/admin/hot-word/synchronize");
    return response.data;
}

/** 将 Vue 热词筛选和排序字段组装成分页请求参数。 */
export function buildAiHotWordQuery(values: {
    keyWord?: string;
    weights?: number[];
    state?: number;
    sortKey?: string;
    sortOrder?: string;
}): AiHotWordQuery {
    const query: AiHotWordQuery = { ...defaultQuery };
    if (values.keyWord) query.keyWord = values.keyWord;
    if (values.weights?.length) query.weights = values.weights;
    if (values.state !== undefined) query.state = values.state;
    if (values.sortKey) query.sortKey = values.sortKey;
    if (values.sortOrder) query.sortOrder = values.sortOrder;
    return query;
}

function formatState(state?: number | string): string {
    return Number(state) === 1 ? "已同步" : "未同步";
}

function isValidHotWord(value: string): boolean {
    return /^[\u4e00-\u9fa50-9]*$/.test(value);
}

/** AI 热词列表，保留 Vue 的筛选、基础 CRUD 和同步入口。 */
export function AiHotWordPage({
    loadHotWords = loadAiHotWords,
    create = createAiHotWord,
    update = updateAiHotWord,
    remove = deleteAiHotWord,
    synchronize = synchronizeAiHotWords
}: AiHotWordPageProps) {
    const navigate = useNavigate();
    const canWrite = useUserStore(state => state.hasPermission("ROLE_ASSISTANTS_WORDS"));
    const banButton = useUserStore(state => state.banButton);
    const writeDisabled = banButton;
    const [rows, setRows] = useState<AiHotWordRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<AiHotWordQuery>(defaultQuery);
    const [keyWord, setKeyWord] = useState("");
    const [weights, setWeights] = useState<number[]>([]);
    const [state, setState] = useState<number | undefined>();
    const [reloadToken, setReloadToken] = useState(0);
    const [formOpen, setFormOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [hotWord, setHotWord] = useState("");
    const [weight, setWeight] = useState<number | undefined>();
    const [formErrors, setFormErrors] = useState<string[]>([]);

    useEffect(() => {
        let disposed = false;
        void loadHotWords(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                message.error("AI热词加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadHotWords, query, reloadToken]);

    const refresh = () => setReloadToken(value => value + 1);

    const submitFilters = () => {
        setQuery(buildAiHotWordQuery({ keyWord, weights, state }));
    };

    const resetFilters = () => {
        setKeyWord("");
        setWeights([]);
        setState(undefined);
        setQuery(defaultQuery);
    };

    const openCreate = () => {
        setEditingId(null);
        setHotWord("");
        setWeight(undefined);
        setFormErrors([]);
        setFormOpen(true);
    };

    const openEdit = (row: AiHotWordRow) => {
        setEditingId(row.id);
        setHotWord(row.hotWord || "");
        setWeight(Number(row.weight));
        setFormErrors([]);
        setFormOpen(true);
    };

    const closeForm = () => {
        setFormOpen(false);
        setFormErrors([]);
    };

    const submitForm = async () => {
        const errors: string[] = [];
        if (!hotWord) errors.push("请输入AI热词");
        else if (!isValidHotWord(hotWord)) errors.push("热词只能输入中文和阿拉伯数字");
        if (weight === undefined) errors.push("请选择权重值");
        setFormErrors(errors);
        if (errors.length || weight === undefined) return;

        const form = { hotWord, weight };
        try {
            if (editingId === null) await create(form);
            else await update(editingId, form);
            closeForm();
            refresh();
            message.success(editingId === null ? "AI热词新增成功" : "AI热词保存成功");
        } catch {
            message.error("AI热词保存失败");
        }
    };

    const runDelete = async (id: number) => {
        try {
            await remove(id);
            refresh();
            message.success("AI热词删除成功");
        } catch {
            message.error("AI热词删除失败");
        }
    };

    const runSynchronize = async () => {
        try {
            await synchronize();
            refresh();
            message.success("AI热词同步成功");
        } catch {
            message.error("AI热词同步失败");
        }
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 热词列表的状态、权限和操作插槽。
    const columns: TableColumnProps<AiHotWordRow>[] = [
        { key: "id", label: "编号", width: 92, prop: "id", render: ({ row }) => row.id },
        { key: "hotWord", label: "AI热词", prop: "hotWord", render: ({ row }) => row.hotWord || DEFAULT_VALUE },
        {
            key: "weight",
            label: "权重值",
            minWidth: 120,
            prop: "weight",
            sortable: true,
            render: ({ row }) => row.weight ?? DEFAULT_VALUE
        },
        {
            key: "state",
            label: "状态",
            width: 106,
            prop: "state",
            render: ({ row }) => <Tag>{formatState(row.state)}</Tag>
        },
        {
            key: "operation",
            label: "操作",
            fixed: "right",
            width: 365,
            prop: "id",
            render: ({ row }) =>
                canWrite ? (
                    <Permission anyOf={["ROLE_ASSISTANTS_WORDS"]}>
                        <Space>
                            <Button
                                aria-label={`编辑热词 ${row.id}`}
                                disabled={writeDisabled}
                                type="link"
                                onClick={() => openEdit(row)}
                            >
                                编辑
                            </Button>
                            <Popconfirm
                                cancelText="取消"
                                okText="确认删除"
                                title="是否删除后此热词将不再展示"
                                onConfirm={() => void runDelete(row.id)}
                            >
                                <Button aria-label={`删除热词 ${row.id}`} disabled={writeDisabled} type="link">
                                    删除
                                </Button>
                            </Popconfirm>
                        </Space>
                    </Permission>
                ) : null
        }
    ];

    return (
        <section className="flex h-full min-h-full flex-col" aria-label="ai-hot-word-page">
            <Card className="flex h-full flex-1 flex-col">
                <ZhTable<AiHotWordRow>
                    data={rows}
                    columns={normalizeOperationColumns(columns)}
                    emptyText="暂无数据"
                    pagination={false}
                    rowKey={row => String(row.id)}
                    onSortChange={({ prop, order }) => {
                        // 仅把权重列排序转换为旧 Vue 接口的 sortKey/sortOrder 参数。
                        if (prop !== "weight") return;
                        setQuery(current => ({
                            ...current,
                            page: 1,
                            sortKey: order ? "weight" : undefined,
                            sortOrder: order || undefined
                        }));
                    }}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Space className="mb-4" wrap>
                                <Typography.Title className="mb-0 mt-0" level={4}>
                                    AI热词
                                </Typography.Title>
                                {canWrite ? (
                                    <Permission anyOf={["ROLE_ASSISTANTS_WORDS"]}>
                                        <Button
                                            aria-label="新增热词"
                                            disabled={writeDisabled}
                                            onClick={openCreate}
                                            type="primary"
                                        >
                                            新增
                                        </Button>
                                    </Permission>
                                ) : null}
                                {canWrite ? (
                                    <Permission anyOf={["ROLE_ASSISTANTS_WORDS"]}>
                                        <Button
                                            aria-label="批量导入"
                                            disabled={writeDisabled}
                                            onClick={() =>
                                                void navigate(`/intelligentAssistant/aiHotWord/upload/${500 - total}`)
                                            }
                                        >
                                            批量导入
                                        </Button>
                                    </Permission>
                                ) : null}
                                {canWrite ? (
                                    <Permission anyOf={["ROLE_ASSISTANTS_WORDS"]}>
                                        <Popconfirm
                                            cancelText="取消"
                                            okText="确认同步"
                                            title="是否一键同步，同步后将覆盖更新之前的所有数据"
                                            onConfirm={() => void runSynchronize()}
                                        >
                                            <Button aria-label="一键同步" disabled={writeDisabled}>
                                                一键同步
                                            </Button>
                                        </Popconfirm>
                                    </Permission>
                                ) : null}
                            </Space>

                            <Space className="mb-4" wrap>
                                <Input
                                    aria-label="热词关键字"
                                    placeholder="请输入热词关键字搜索"
                                    value={keyWord}
                                    onChange={event => setKeyWord(event.target.value)}
                                />
                                <Select
                                    aria-label="权重值筛选"
                                    mode="multiple"
                                    placeholder="请选择权重值"
                                    value={weights}
                                    options={weightOptions.map(value => ({ label: String(value), value }))}
                                    onChange={values => setWeights(values)}
                                />
                                <Select
                                    aria-label="同步状态筛选"
                                    allowClear
                                    placeholder="请选择状态"
                                    value={state}
                                    options={[
                                        { label: "未同步", value: 0 },
                                        { label: "已同步", value: 1 }
                                    ]}
                                    onChange={value => setState(value)}
                                />
                                <Button aria-label="查询热词" type="primary" onClick={submitFilters}>
                                    查询
                                </Button>
                                <Button aria-label="重置热词" onClick={resetFilters}>
                                    重置
                                </Button>
                            </Space>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                hideOnSinglePage
                                onChange={(page, size) => setQuery(current => ({ ...current, page, size }))}
                            />
                        </div>
                    }
                />
                <ZhDialog
                    open={formOpen}
                    title={editingId === null ? "新增热词" : "编辑热词"}
                    width={420}
                    onCancel={closeForm}
                    footer={
                        <Space>
                            <Button onClick={closeForm}>取消</Button>
                            <Button disabled={writeDisabled} type="primary" onClick={() => void submitForm()}>
                                提交热词
                            </Button>
                        </Space>
                    }
                >
                    {formErrors.map(error => (
                        <Alert key={error} className="mb-2" message={error} role="alert" type="error" />
                    ))}
                    <Space orientation="vertical" className="w-full">
                        <Input
                            aria-label="AI热词输入"
                            maxLength={10}
                            placeholder="请输入AI热词"
                            value={hotWord}
                            onChange={event => setHotWord(event.target.value)}
                        />
                        <Select
                            aria-label="权重值输入"
                            className="w-full"
                            placeholder="请选择权重值"
                            value={weight}
                            options={weightOptions.map(value => ({ label: String(value), value }))}
                            onChange={value => setWeight(value)}
                        />
                    </Space>
                </ZhDialog>
            </Card>
        </section>
    );
}

export default AiHotWordPage;
